import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toLocalIso } from '../utils/dateValueHelper';
import publicBookingApi from '../services/publicBookingApi';
import '../styles/CustomDatePicker.css';

/**
 * CustomDatePicker - Calendar with availability visualization
 *
 * Props:
 * - propertyId: MongoDB property ID
 * - lang: 'ar' or 'en'
 * - selectedDate: Currently selected date (single mode)
 * - startDate: Range start date (range mode)
 * - endDate: Range end date (range mode)
 * - onChange: Callback when date is selected
 * - minDate: Minimum selectable date
 * - placeholderText: Placeholder text for input
 * - excludeDates: Array of dates to exclude (optional)
 * - selectsRange: Enable range selection mode (default: false)
 */
const CustomDatePicker = ({
  propertyId,
  lang = 'ar',
  selectedDate,
  startDate,
  endDate,
  onChange,
  minDate,
  placeholderText,
  excludeDates = [],
  selectsRange = false
}) => {
  const [availabilityCalendar, setAvailabilityCalendar] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        const response = await publicBookingApi.getAvailabilityCalendar(propertyId, lang, 60);

        if (response.success) {
          setAvailabilityCalendar(response.calendar);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
      } finally {
        setLoading(false);
      }
    };

    if (propertyId) {
      fetchAvailability();
    }
  }, [propertyId, lang]);

  // Get array of unavailable dates
  const getUnavailableDates = () => {
    return availabilityCalendar
      .filter(day => !day.available)
      .map(day => new Date(day.date));
  };

  // Check if a date is available
  const isDateAvailable = (date) => {
    if (!date) return false;

    const dateStr = toLocalIso(date);
    const dayRecord = availabilityCalendar.find(d => d.date === dateStr);

    return dayRecord ? dayRecord.available : false;
  };

  // Custom day class name to color available/unavailable dates
  const getDayClassName = (date) => {
    if (!date) return '';

    const dateStr = toLocalIso(date);
    const dayRecord = availabilityCalendar.find(d => d.date === dateStr);

    if (!dayRecord) return 'date-unknown';

    return dayRecord.available ? 'date-available' : 'date-unavailable';
  };

  // Combine excluded dates with unavailable dates
  const allExcludedDates = [
    ...excludeDates,
    ...getUnavailableDates()
  ];

  return (
    <div className={`custom-datepicker-wrapper ${lang === 'ar' ? 'rtl' : 'ltr'}`}>
      <DatePicker
        selected={selectsRange ? undefined : selectedDate}
        startDate={selectsRange ? startDate : undefined}
        endDate={selectsRange ? endDate : undefined}
        selectsRange={selectsRange}
        onChange={onChange}
        minDate={minDate}
        excludeDates={allExcludedDates}
        placeholderText={placeholderText}
        dateFormat="yyyy-MM-dd"
        dayClassName={getDayClassName}
        disabled={loading}
        className="date-picker-input"
        calendarClassName="custom-calendar"
        popperClassName="custom-popper"
        showPopperArrow={false}
        autoComplete="off"
        // Month/year dropdowns for easier navigation
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        // Render custom day content with availability indicator
        renderDayContents={(day, date) => {
          const isAvailable = isDateAvailable(date);
          return (
            <div className="day-content">
              <span className="day-number">{day}</span>
              {!loading && (
                <span className={`day-indicator ${isAvailable ? 'available' : 'unavailable'}`}>
                  {isAvailable ? '●' : '●'}
                </span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export default CustomDatePicker;
